import { useState } from 'react';
import type { User } from '@faktorplus/shared';
import { Button } from '../../components/ui/button';
import { FilterIcon } from '../../components/ui/icons';
import { Field, Input, Select } from '../../components/ui/form';
import { Modal, modalStyles } from '../../components/ui/modal';
import { formatMoney } from '../../lib/format';
import styles from '../invoices/invoice-filters.module.css';

export type ProjectStateFilter = 'active' | 'finished' | 'all';
export type ProjectSortKey = 'cost' | 'name' | 'contract' | 'invoices';

export interface ProjectFilters {
  search: string;
  state: ProjectStateFilter;
  managerId: string;
  /** Net cost bounds in złoty; `undefined` means the bound is open. */
  costMin?: number;
  costMax?: number;
  sortBy: ProjectSortKey;
}

const STATE_OPTIONS: { value: ProjectStateFilter; label: string }[] = [
  { value: 'active', label: 'W toku' },
  { value: 'finished', label: 'Zakończone' },
  { value: 'all', label: 'Wszystkie' },
];

const SORT_OPTIONS: { value: ProjectSortKey; label: string }[] = [
  { value: 'cost', label: 'Koszty malejąco' },
  { value: 'name', label: 'Nazwa A–Z' },
  { value: 'contract', label: 'Kontrakt malejąco' },
  { value: 'invoices', label: 'Najwięcej faktur' },
];

const STATE_LABEL = Object.fromEntries(STATE_OPTIONS.map((o) => [o.value, o.label])) as Record<
  ProjectStateFilter,
  string
>;
const SORT_LABEL = Object.fromEntries(SORT_OPTIONS.map((o) => [o.value, o.label])) as Record<
  ProjectSortKey,
  string
>;

/** The dialog's keys; `search` stays in the bar. */
const DIALOG_KEYS = ['state', 'managerId', 'costMin', 'costMax', 'sortBy'] as const;
type DialogKey = (typeof DIALOG_KEYS)[number];
type Draft = Record<DialogKey, string>;

interface ProjectFiltersBarProps {
  params: URLSearchParams;
  /** Site managers see only their own sites: no state or manager choice. */
  isOffice: boolean;
  managers: User[];
  onChange: (patch: Record<string, string>) => void;
  onReset: () => void;
}

/**
 * The site list's filter bar — same shape as the register's: search inline,
 * everything else behind one button, every hidden choice restated as a chip.
 */
export function ProjectFiltersBar({
  params,
  isOffice,
  managers,
  onChange,
  onReset,
}: ProjectFiltersBarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const chips = describeActive(params, managers);
  const activeCount = chips.length;

  return (
    <>
      <div className={styles.bar}>
        <Input
          className={styles.search}
          placeholder="Szukaj budowy…"
          aria-label="Szukaj budowy"
          value={params.get('search') ?? ''}
          onChange={(event) => onChange({ search: event.target.value })}
        />

        <Button
          className={activeCount > 0 ? styles.ctaActive : undefined}
          onClick={() => setIsOpen(true)}
          aria-expanded={isOpen}
        >
          <FilterIcon className={styles.filterIcon} />
          Filtry
          {activeCount > 0 && <span className={styles.count}>{activeCount}</span>}
        </Button>
      </div>

      {chips.length > 0 && (
        <div className={styles.chips}>
          {chips.map((chip) => (
            <span key={chip.key} className={styles.chip}>
              <span className={styles.chipLabel}>{chip.label}</span>
              <span className={styles.chipValue}>{chip.value}</span>
              <button
                type="button"
                className={styles.chipRemove}
                aria-label={`Usuń filtr: ${chip.label}`}
                onClick={() => onChange(chip.clear)}
              >
                ×
              </button>
            </span>
          ))}
          <button type="button" className={styles.clearAll} onClick={onReset}>
            Wyczyść wszystkie
          </button>
        </div>
      )}

      {isOpen && (
        <FilterDialog
          isOffice={isOffice}
          managers={managers}
          initial={readDraft(params)}
          onClose={() => setIsOpen(false)}
          onApply={(draft) => {
            onChange(draft);
            setIsOpen(false);
          }}
        />
      )}
    </>
  );
}

interface FilterDialogProps {
  isOffice: boolean;
  managers: User[];
  initial: Draft;
  onApply: (draft: Draft) => void;
  onClose: () => void;
}

/** Edits a draft and commits it on "Zastosuj", like the register's dialog. */
function FilterDialog({ isOffice, managers, initial, onApply, onClose }: FilterDialogProps) {
  const [draft, setDraft] = useState<Draft>(initial);

  function set(key: DialogKey, value: string) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  return (
    <Modal
      title="Filtry budów"
      isOpen
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={() => setDraft(emptyDraft())}>
            Wyczyść
          </Button>
          <span className={modalStyles.footerSpacer} />
          <Button onClick={onClose}>Anuluj</Button>
          <Button variant="primary" onClick={() => onApply(draft)}>
            Zastosuj
          </Button>
        </>
      }
    >
      {isOffice && (
        <Field label="Stan budowy">
          {(id) => (
            <Select id={id} value={draft.state} onChange={(e) => set('state', e.target.value)}>
              {STATE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}

      {isOffice && (
        <Field label="Kierownik budowy">
          {(id) => (
            <Select
              id={id}
              value={draft.managerId}
              onChange={(e) => set('managerId', e.target.value)}
            >
              <option value="">Wszyscy kierownicy</option>
              {managers.map((manager) => (
                <option key={manager.id} value={manager.id}>
                  {manager.fullName}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}

      <Field label="Koszty netto" hint="Kwoty w złotych; jedna granica wystarczy.">
        {(id) => (
          <div className={styles.range}>
            <Input
              id={id}
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              placeholder="od"
              aria-label="Koszty netto od"
              value={draft.costMin}
              onChange={(e) => set('costMin', e.target.value)}
            />
            <span className={styles.rangeSeparator}>—</span>
            <Input
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              placeholder="do"
              aria-label="Koszty netto do"
              value={draft.costMax}
              onChange={(e) => set('costMax', e.target.value)}
            />
          </div>
        )}
      </Field>

      <Field label="Sortowanie">
        {(id) => (
          <Select id={id} value={draft.sortBy} onChange={(e) => set('sortBy', e.target.value)}>
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        )}
      </Field>
    </Modal>
  );
}

/* ───────────────────────────── URL ⇄ filters ───────────────────────────── */

export function readProjectFilters(params: URLSearchParams): ProjectFilters {
  const state = params.get('state');
  const sortBy = params.get('sortBy');

  return {
    search: params.get('search') ?? '',
    state: STATE_OPTIONS.some((o) => o.value === state) ? (state as ProjectStateFilter) : 'active',
    managerId: params.get('managerId') ?? '',
    costMin: readAmount(params.get('costMin')),
    costMax: readAmount(params.get('costMax')),
    sortBy: SORT_OPTIONS.some((o) => o.value === sortBy) ? (sortBy as ProjectSortKey) : 'cost',
  };
}

/** Whether anything narrows the list — an empty result then means "no match". */
export function hasAnyProjectFilter(params: URLSearchParams): boolean {
  return ['search', 'state', 'managerId', 'costMin', 'costMax'].some((key) => params.get(key));
}

function readAmount(value: string | null): number | undefined {
  if (!value) return undefined;
  const amount = Number(value.replace(',', '.'));
  return Number.isFinite(amount) && amount >= 0 ? amount : undefined;
}

function readDraft(params: URLSearchParams): Draft {
  const filters = readProjectFilters(params);
  return {
    state: filters.state,
    managerId: filters.managerId,
    costMin: params.get('costMin') ?? '',
    costMax: params.get('costMax') ?? '',
    sortBy: filters.sortBy,
  };
}

function emptyDraft(): Draft {
  return { state: 'active', managerId: '', costMin: '', costMax: '', sortBy: 'cost' };
}

interface Chip {
  key: string;
  label: string;
  value: string;
  clear: Record<string, string>;
}

function describeActive(params: URLSearchParams, managers: User[]): Chip[] {
  const filters = readProjectFilters(params);
  const chips: Chip[] = [];

  if (filters.state !== 'active') {
    chips.push({ key: 'state', label: 'Stan', value: STATE_LABEL[filters.state], clear: { state: '' } });
  }

  if (filters.managerId) {
    const manager = managers.find((m) => m.id === filters.managerId);
    chips.push({
      key: 'managerId',
      label: 'Kierownik',
      value: manager?.fullName ?? '…',
      clear: { managerId: '' },
    });
  }

  const cost = describeCostRange(filters);
  if (cost) {
    chips.push({ key: 'cost', label: 'Koszty netto', value: cost, clear: { costMin: '', costMax: '' } });
  }

  if (filters.sortBy !== 'cost') {
    chips.push({
      key: 'sortBy',
      label: 'Sortowanie',
      value: SORT_LABEL[filters.sortBy],
      clear: { sortBy: '' },
    });
  }

  return chips;
}

function describeCostRange(filters: ProjectFilters): string | null {
  const { costMin, costMax } = filters;
  if (costMin !== undefined && costMax !== undefined) {
    return `${formatMoney(costMin)} – ${formatMoney(costMax)}`;
  }
  if (costMin !== undefined) return `od ${formatMoney(costMin)}`;
  if (costMax !== undefined) return `do ${formatMoney(costMax)}`;
  return null;
}
